//////////////////////////////////////////////////////////////////////////////
// GIS Air Combat Simulation Demo
//
// This program simulates a missile fight. It creates a number of planes
// and missiles, and animates them. Every missile is following one plane 
// until it destroys the plane. Initailly, the missiles are going slower
// than the planes, but eventually they are catching up.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files. The GLG library loads a GLG drawing
// and renders it on a web page, providing an API to handle user interaction
// with graphical objects in the drawing.
//
// The drawings are created using the GLG Graphics Builder, an interactive
// editor that allows to create grahical objects and define their dynamic
// behavior without any programming. The airplane and missile icons are dynamic
// GLG objects created with the Graphics Builder.
//
// The background map is generated by the GLG Map Server, which is integrated
// inside of a GLG drawing as a GLG GIS Object. The GIS Object takes a
// complete care of the map display, automatically generating Map Server
// requests to re-generate the image when the map is panned or zoomed.
//
// Except for the changes to comply with the JavaScript syntax, this source
// is identical to the source code of the corresponding C/C++/C# and Java
// desktop versions of the demo.
//
// This demo uses the GLG Map Server to display a map.
// The Map Server has to be installed either on the local host or on a
// remote web server. After the Map Server has been installed, modify the
// source code to set the SuppliedMapServerURL variable to point to the
// Map Server location.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* This demo uses the GLG Map Server to display a map.
   The Map Server has to be installed either on the local host or on a
   remote web server. After the Map Server has been installed, set the 
   SuppliedMapServerURL variable to point to the Map Server location:

   For example on Linux:
     SuppliedMapServerURL = "http://localhost/cgi-bin/GlmScript";
   or
     SuppliedMapServerURL = "http://www.myserver.com/cgi-bin/GlmScript";

   On Windows:
     SuppliedMapServerURL = "http://localhost/Scripts/GlmScript.pl";
   or
     SuppliedMapServerURL = "http://www.myserver.com/Scripts/GlmScript.pl";

   The SuppliedMapServerURL value overrides the URL of the GIS object in the
   drawing.
*/
const SuppliedMapServerURL = null;    /* String */

const UPDATE_INTERVAL = 30;        /* Update interval in msec */

// Simulation parameters.
const NUM_PLANE_MISSILE_PAIRS = 8; /* Number of airplains */
const MAX_DISTANCE      = 0.8;     /* Max allowed distance from the center,
                                      in relative units. */
const DESTROY_DISTANCE  = 0.03;    /* How close (relative units) a missile 
                                      needs to get to a plane to destroy it. */
const CHANGE_THRESHOLD  = 0.99;    /* The number in the range [0;1]; defines 
                                      how often planes change direction. */
const PLANE_SPEED       = 0.01;    /* Plane speed */
const MISSILE_SPEED     = 0.6;     /* Initial missile speed relatively to a
                                      plane. */
const TRANSITION_NUM    = 20;      /* The number of steps required for a turn */
const DELAY             =  2;      /* Delay after a turn */
const MISSILE_DELAY     = 10;      /* Missile adjustment delay */
const BLAST_DURATION    = 10;       /* Number of iterations used to animate a 
                                       blast icon. */
const BLAST_SCALE       = 0.1;    /* Initial scale of the blast icon. */

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the air combat demo.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgAirCombat( glg_div_name, glg_path, is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
    
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   // Top level viewport of the loaded drawing.
   this.Drawing = null;              /* GlgObject */
   this.MapViewport = null;          /* GlgObject */
   this.GISObject = null;            /* GlgObject */
   this.PlaneTemplate = null;        /* GlgObject */
   this.MissileTemplate = null;      /* GlgObject */
   this.BlastTemplate = null;        /* GlgObject */
   this.InfoDialog = null;           /* GlgObject */

   /* Both missiles and airplanes use the same Plane structure.
      NumPlanes is the total number of Plane structires.
   */      
   this.NumPlanes = NUM_PLANE_MISSILE_PAIRS * 2;        /* int */
   this.NumActivePlanes = 0;                            /* int */
   this.NumBlasts = 0;                                  /* int */
   this.SelectedPlaneIndex = -1;                        /* int */
   
   // Increase initial plane size on mobile devices.
   this.PlaneScale = ( this.IsMobile ? 1.2 : 1.0 );     /* double */

   this.UpdateSpeed = 1.0;           /* double */
   this.MaxDistance = null;          /* double */

   /* Stores GIS center */
   this.Center = GLG.CreateGlgPoint( 0, 0, 0 );         /* GlgPoint */

   // Temporary variables: allocate once.
   this.BufPosition1 = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.BufPosition2 = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.BufPlane = new Plane();                         /* Plane */

   this.MapLoaded = false;   /* boolean */

   // Arrays that keeps the state of the simulation.
   this.Planes = [];         /* Plane[] - stores both planes and missiles. */
   this.BlastArray = [];     /* Blast[] */

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts air combat demo by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgAirCombat.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   /* Load a drawing from the specified drawing file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "aircombat.g" ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgAirCombat.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgAirCombat.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Drawing )
     this.Drawing.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

//////////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.LoadCB = function( drawing, data, path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }

   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartAirCombatDemo( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.StartAirCombatDemo = function( /* GlgObject */ drawing )
{
   this.Drawing = drawing;

   if( SuppliedMapServerURL != null )
     // Override the URL defined in the drawing.
     this.Drawing.SetSResource( "Map/GISObject/GISMapServerURL",
                           SuppliedMapServerURL );
    
   /* Both missiles and airplanes use the same Plane structure.
      NumPlanes is the total number of Plane structires.
   */
      
   this.NumPlanes = NUM_PLANE_MISSILE_PAIRS * 2;
   this.Planes = new Array( this.NumPlanes );

   /* Load plane icons and delete the icon palette from the drawing
      before it becomes visible.
   */
   this.LoadPlaneIcons();

   this.MapViewport = this.Drawing.GetResourceObject( "Map" );
   this.InfoDialog = this.MapViewport.GetResourceObject( "Info" );
   this.GISObject = this.MapViewport.GetResourceObject( "GISObject" );
   this.GetMapExtent();
   
   this.InfoDialog.SetDResource( "Visibility", 0.0 );
   this.Drawing.SetDResource( "UpdateSpeed/ValueX", this.UpdateSpeed );

   /* Add Input callback used to handle user interaction. */
   this.Drawing.AddListener( GLG.GlgCallbackType.INPUT_CB,
                             this.InputCallback.bind( this ) );

   this.MapViewport.SetDResource( "LoadingMessage/Visibility", 1.0 );

   this.Drawing.SetupHierarchy();

   // Populate the drawing with planes: copy the plane template.
   this.CreatePlanes( this.NumPlanes );

   this.Drawing.Update();

   setTimeout( ()=>this.UpdateBattlefield(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.GetMapExtent = function()
{
   this.Center = this.GISObject.GetGResource( "GISCenter" );
   let extent = this.GISObject.GetGResource( "GISExtent" );   /* GlgPoint */
   this.MaxDistance = Math.min( extent.x / 2.0, extent.y / 2.0 ) * MAX_DISTANCE;
}

//////////////////////////////////////////////////////////////////////////
// Loads plane icons from the plane palette defined in the drawing and
// select the icons to use depending on the number of planes.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.LoadPlaneIcons = function()
{
   let  /* GlgObject */
     plane_palette, Plane3DFull, Plane3DLight, Plane2D, Missile3D, Missile2D;
    
   /* Find a plane palette. */
   plane_palette = this.Drawing.GetResourceObject( "PlanePalette" );
   
   // Find templates in the drawing
   Plane3DFull  = plane_palette.GetResourceObject( "plane_3D_full" );
   Plane3DLight = plane_palette.GetResourceObject( "plane_3D_light" );
   Plane2D      = plane_palette.GetResourceObject( "plane_3D_light" );
   Missile3D    = plane_palette.GetResourceObject( "missile_3D_full" );
   Missile2D    = plane_palette.GetResourceObject( "missile_2D" );
   
   this.BlastTemplate = plane_palette.GetResourceObject( "blast" );   

   // Delete the palette from the drawing 
   this.Drawing.DeleteThisObject( plane_palette );
   
   if( this.NumPlanes <= 30 )
   {
      this.PlaneTemplate = Plane3DFull;
      this.MissileTemplate = Missile3D; 
   }
   else if( this.NumPlanes <= 80 )
   {
      this.PlaneTemplate = Plane3DLight;
      this.MissileTemplate = Missile3D; 
   }
   else
   {
      this.PlaneTemplate = Plane2D;
      this.MissileTemplate = Missile2D; 
   }
}

//////////////////////////////////////////////////////////////////////////
// Creates planes by copying the template and adds the planes to the
// drawing. Annotates missiles with a different size and color.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.CreatePlanes = function( /* int */ num_planes )
{
   for( let i=0; i<num_planes; ++i )
   {
      // Create a copy of a plane template and add it to the drawing

      let plane = new Plane();
      this.Planes[ i ] = plane;
      
      let name;
      if( IS_MISSILE( i ) )
      {         
         plane.glg_object = this.MissileTemplate.CopyObject();
         name = "Missile " + i;
      }
      else
      {
         plane.glg_object = this.PlaneTemplate.CopyObject();
         name = "Plane " + i;
      }
      
      // Store name.
      plane.glg_object.SetSResource( "Name", name );
      
      /* Store a plane index used to identify the plane when it is selected 
         with the mouse. It is stored in a custom property attached to the 
         plane icon.
      */
      plane.glg_object.SetDResource( "PlaneIndex", i );

      /* Add planes and missiles to GISArray of GISObject to be able to 
         position them using lat/lon coordinates.
      */
      this.GISObject.AddObjectToBottom( plane.glg_object );

      // Set initial simulation parameters 
      plane.destroyed = false;
      plane.correcting_distance = 0;
      plane.missile_delay = 0;
      
      if( IS_PLANE( i ) )
      {
         // It's a plane: set a random initial position
         plane.position.x =
           this.Center.x + this.MaxDistance * GetData( -1.0, 1.0 );
         plane.position.y =
           this.Center.y + this.MaxDistance * GetData( -1.0, 1.0 );
         plane.position.z =
           this.Center.z + this.MaxDistance * GetData( -1.0, 1.0 );
      }
      else
      {
         // It's a missile: set in some proximity of a plane.
         plane.position.x = this.Planes[ i - 1 ].position.x +
           this.MaxDistance * GetData( -0.3, 0.3 );
         plane.position.y = this.Planes[ i - 1 ].position.y +
           this.MaxDistance * GetData( -0.3, 0.3 );
         // Set at the same depth.
         plane.position.z = this.Planes[ i - 1 ].position.z;
      }

      if( IS_MISSILE( i ) )   // Missile
        // Set missiles initial speed to be lower 
        plane.speed = MISSILE_SPEED * PLANE_SPEED;         
      else   // Plane 
        plane.speed = PLANE_SPEED;
      
      plane.change_state = 1;
      this.SetPlane( i );     // Set initial plane parameters. 
      plane.change_state = 0;
   }
   this.NumActivePlanes = num_planes;
   
   this.AdjustPlanesScale( this.PlaneScale ); // Set initial plane scale 
}

//////////////////////////////////////////////////////////////////////////
// Simulates a plane or a missile.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.AutoPilot = function( /* int */ plane_index )
{
   let plane = this.Planes[ plane_index ];

   if( plane.destroyed )
     return;
   
   if( IS_PLANE( plane_index ) )
   {
      if( Distance( plane.position, this.Center ) > this.MaxDistance )
      {
         // Change direction to correct the distance from a center.
         if( plane.correcting_distance == 0 )
           this.ChangeDirection( plane_index, true );
      }
      else if( GetData( 0.0, 1.0 ) > CHANGE_THRESHOLD )
        this.ChangeDirection( plane_index, false );  // Random direction change 
   }
   else   // Missile 
   {
      ++plane.missile_delay;
      if( ( plane.missile_delay % MISSILE_DELAY ) == 0 )
        // Adjust missile to the new plane position 
        this.ChangeDirection( plane_index, false );
   }
   
   // Move the plane by one simulation step. 
   this.MakeStep( plane_index, 1, null );
   
   if( !plane.destroyed )
     this.SetPlane( plane_index );  // Set the plane in the drawing 
}

//////////////////////////////////////////////////////////////////////////
// For a missile: adjust the missile's direction to point to the plane it
//   follows.
// For a plane:
//   a) if required==True, change the plane direction to correct the
//      distance from the center to avoid going off the screen.
//   b) if required==False, randomly change the plane's direction to make
//      it more complicated for a missile to catch it.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.ChangeDirection =
  function( /* int */ plane_index, /* boolean */ required )
{
   let dx, dy, dz, angle_y, angle_z;   /* double */

   if( IS_PLANE( plane_index ) )
   {
      let plane = this.Planes[ plane_index ];
   
      // Start a new change and set flags to indicate a change in progress.
      plane.change_state = TRANSITION_NUM - 1; 
      if( required )
        plane.correcting_distance = DELAY * TRANSITION_NUM + 1;
      
      while( true )
      {
         // Choose a new random direction 
         plane.angle_change.x = 60.0 * GetData( -1.0, 1.0 ) / TRANSITION_NUM;
         plane.angle_change.y = 60.0 * GetData( -1.0, 1.0 ) / TRANSITION_NUM;
         plane.angle_change.z = 360.0 * GetData( 0.0, 1.0 ) / TRANSITION_NUM;

         // Test if it lowers the distance from the center, if required. 
         if( !required ||
             this.DecreaseDistance( plane_index,
                                    0, DELAY * TRANSITION_NUM + 1 ) )
           break;
      }
   }
   else  // Missile 
   {
      let missile = this.Planes[ plane_index ];
      let plane = this.Planes[ plane_index - 1 ];
      
      // Calculate the angles to direct the missile at the plane. 
      dx = plane.position.x - missile.position.x;
      dy = plane.position.y - missile.position.y;
      dz = plane.position.z - missile.position.z;
      
      if( dx == 0.0 )
        angle_z = ( dy > 0.0 ? 90.0 : 270.0 );
      else
      {
         angle_z = Math.atan( dy / dx );
         angle_z = DEG( angle_z );
         if( dx < 0.0 )
           angle_z += 180.0;
      }
      
      if( dx == 0.0 )
        angle_y = ( dz > 0.0 ? 270.0 : 90.0 );
      else
      {
         angle_y = Math.atan( dz / dx );
         angle_y = DEG( angle_y );
      }
      
      // Change missile direction instantly (it has a much smaller inertia). 
      
      // Take x angle from a plane. 
      missile.angle.x = plane.angle.x; 
      missile.angle.y = angle_y;
      missile.angle.z = angle_z;
      missile.change_state = 1;
      
      // Randomly speed the missile up until it is faster then the plane. 
      if( missile.speed < 1.2 * PLANE_SPEED && GetData( 0.0, 1.0 ) > 0.5 )
        missile.speed *= 1.04;
   }
}

//////////////////////////////////////////////////////////////////////////
// Calculate and return a distance between two positions.
//////////////////////////////////////////////////////////////////////////
function Distance( /* GlgPoint */ position1,
                   /* GlgPoint */ position2 )   /* double */
{
   let dx, dy, dz;    /* double */

   dx = position1.x - position2.x;
   dy = position1.y - position2.y;
   dz = position1.z - position2.z;
   
   return Math.sqrt( dx * dx + dy * dy + dz * dz );
}

//////////////////////////////////////////////////////////////////////////
/// Tests whether or not the distance will decrease after the required
/// number of simulation steps. Compares the distance after steps1 number
/// of steps with the distance after steps2 number of steps.
///////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.DecreaseDistance =    /* boolean */
  function( /* int */ plane_index, /* int */ steps1, /* int */ steps2 )  
{
   this.MakeStep( plane_index, steps1, this.BufPosition1 );
   this.MakeStep( plane_index, steps2, this.BufPosition2 );
   return ( Distance( this.BufPosition2, this.Center ) <
            Distance( this.BufPosition1, this.Center ) );
}

//////////////////////////////////////////////////////////////////////////
// Makes the requested number of simulation steps and returns the
// calculated postion of a plane after that.
// If the position_value parameter is not NULL, it is a trial step; the
// calculated values are returned in this structure and the plane
// parametrs are restored afterwards.
// If the parameter is NULL, it is a real step affecting the plane.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.MakeStep =
  function( /* int */ plane_index, /* int */ num_steps,
            /* GlgPoint */ position )
{
   let plane = this.Planes[ plane_index ];
   
   if( position != null )
     this.BufPlane.CopyPlaneInfoFrom( plane );     // Save
   
   for( let i=0; i<num_steps; ++i )
   {
      // Make a step in the current direction 
      plane.position.x += plane.speed * this.UpdateSpeed *
        Math.cos( RAD( plane.angle.z ) ) * Math.cos( RAD( plane.angle.y ) );

      plane.position.y += plane.speed * this.UpdateSpeed *
        Math.sin( RAD( plane.angle.z ) ) * Math.cos( RAD( plane.angle.y ) );

      plane.position.z += plane.speed * this.UpdateSpeed *
        Math.sin( RAD( plane.angle.y ) ) * Math.cos( RAD( plane.angle.z ) );

      // If a direction change is in progress, adjust the plane angles. 
      if( plane.change_state != 0 && IS_PLANE( plane_index ) )
      {
         plane.angle.x += plane.angle_change.x;
         plane.angle.y += plane.angle_change.y;
         plane.angle.z += plane.angle_change.z;
         
         if( plane.angle.x >= 360.0 )
           plane.angle.x -= 360.0;
         if( plane.angle.y >= 360.0 )
           plane.angle.y -= 360.0;
         if( plane.angle.z >= 360.0 )
           plane.angle.z -= 360.0;
         
         --plane.change_state;
      }
      
      if( plane.correcting_distance != 0 )
        --plane.correcting_distance;
   }

   if( position != null ) // Just checking the distance: restore and return.
   {
      // Return the result 
      position.x = plane.position.x;
      position.y = plane.position.y;
      position.z = plane.position.z;
      
      plane.CopyPlaneInfoFrom( this.BufPlane );     // Restore
   }
   else  // Real step: if it is a missile, check if it got its plane. 
     if( IS_MISSILE( plane_index ) )
     {
        plane = this.Planes[ plane_index - 1 ];
        let missile = this.Planes[ plane_index ];
         
        if( Distance( plane.position,
                      missile.position ) < DESTROY_DISTANCE * this.MaxDistance )
        {
           /* Got it: Delete both the plane and the missile, and replace them
              with a blast icon.
           */
           this.DeletePlane( plane_index - 1, false );
           this.DeletePlane( plane_index, false );
        }
     }
}

//////////////////////////////////////////////////////////////////////////
// Deletes the plane or missile from the drawing. Flashes the size of a
// missile for an explosive effect.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.DeletePlane =
  function( /* int */ plane_index, /* boolean */ deleting_all )
{
   let plane = this.Planes[ plane_index ];

   if( plane_index == this.SelectedPlaneIndex )
     this.UnselectPlane();

   if( IS_PLANE( plane_index ) )
     this.AddBlast( plane.position );

   // Delete the plane from the drawing.
   if( !this.GISObject.DeleteThisObject( plane.glg_object ) )
     GLG.Error( GLG.GlgErrorType.WARNING, "Cannot delete the plane." );

   plane.destroyed = true;
   --this.NumActivePlanes;
}

//////////////////////////////////////////////////////////////////////////
// Add a blast icon at the last plane position.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.AddBlast = function( /* GlgPoint */ position )
{
   let blast = new Blast();
   blast.glg_object = this.BlastTemplate.CopyObject();
   blast.glg_object.SetDResource( "Scale", BLAST_SCALE );
   blast.glg_object.SetGResource( "Position",
                                  position.x, position.y, position.z );   
   
   // Add to the blast array.
   this.BlastArray.push( blast );

   // Add to the drawing.
   this.GISObject.AddObjectToBottom( blast.glg_object );
   ++this.NumBlasts;
}

//////////////////////////////////////////////////////////////////////////
// Delete a blast icon.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.DeleteBlast = function( /* Blast */ blast )
{
   // Delete the blast from the drawing.
   if( !this.GISObject.DeleteThisObject( blast.glg_object ) )
     GLG.Error( GLG.GlgErrorType.WARNING,
                "Cannot delete the blast icon from the drawing." );

   // Delete from the blast array.
   let index = this.BlastArray.indexOf( blast );
   if( index > -1 )
     this.BlastArray.splice( index, 1 );
   else
     GLG.Error( GLG.GlgErrorType.WARNING, "Cannot delete the blast icon." );

   --this.NumBlasts;
}

//////////////////////////////////////////////////////////////////////////
// Updates the plane in the drawing with the new values. Ignores 3D
// rotation for a missle.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.SetPlane = function( /* int */ plane_index )
{
   let plane = this.Planes[ plane_index ];

   // Plane is changing its direction. 
   if( plane.change_state != 0 )
   {
      plane.glg_object.SetDResource( "AngleX", plane.angle.x );
      plane.glg_object.SetDResource( "AngleY", plane.angle.y );
      plane.glg_object.SetDResource( "AngleZ", plane.angle.z );

      if( IS_MISSILE( plane_index ) )     // Missile: an instant change 
        plane.change_state = 0;
   }
   
   plane.glg_object.SetGResource( "Position", plane.position.x,
                                  plane.position.y, plane.position.z );
}

//////////////////////////////////////////////////////////////////////////
// Makes one simulation step for every plane or missile.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.UpdateBattlefield = function()
{
   if( !this.Active )
     return;
   
   if( !this.MapLoaded &&
       Math.trunc( this.GISObject.GetDResource( "ImageLoaded" ) ) != 0 )
   {
      // Erase map loading message.
      this.MapViewport.SetDResource( "LoadingMessage/Visibility", 0 );
      this.MapLoaded = true;
   }

   if( this.NumBlasts )
     this.AgeBlasts();
   
   if( this.NumActivePlanes )
     for( let i=0; i<this.NumPlanes; ++i )
       this.AutoPilot( i );
   else
     /* All planes are gone: Wait for all blasts to be erased, then
        recreate planes to restart a new round.
     */
     if( !this.NumBlasts )
       this.CreatePlanes( this.NumPlanes );
   
   this.DisplaySelectedPlaneInfo();
   
   // Update the drawing after changes. 
   this.Drawing.Update();

   // Restart update timer.
   setTimeout( ()=>this.UpdateBattlefield(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.AgeBlasts = function()
{
   // Age/delete in reverce order.
   for( let i=this.NumBlasts-1; i>=0; --i )
   {
      let blast = this.BlastArray[ i ];
      --blast.age;
      if( blast.age <= 0 )
        this.DeleteBlast( blast );
      else
      {
         // Fade it out by reducing its scale.
         let scale = BLAST_SCALE * blast.age / BLAST_DURATION;
         blast.glg_object.SetDResource( "Scale", scale );
      }
   }
}

//////////////////////////////////////////////////////////////////////////
// Returns a random number in the requested range.
//////////////////////////////////////////////////////////////////////////
function GetData( /* double */ min, /* double */ max )   /* double */
{
   return GLG.Rand( min, max );
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.DisplaySelectedPlaneInfo = function()
{
   if( this.SelectedPlaneIndex == -1 )
     return;

   let plane = this.Planes[ this.SelectedPlaneIndex ];
   
   let roll = plane.angle.x;
   let pitch = plane.angle.y;
   let yaw = plane.angle.z - 90.0;
   
   while( roll > 180.0 )
     roll -= 360.0;
   while( pitch > 180.0 )
     pitch -= 360.0;
   while( yaw > 180.0 )
     yaw -= 360.0;
   
   this.InfoDialog.SetDResource( "Roll",  roll );
   this.InfoDialog.SetDResource( "Pitch", pitch );
   this.InfoDialog.SetDResource( "Yaw",   yaw );
   
   let lat_string = CreateLocationString( plane.position.y, true );
   let lon_string = CreateLocationString( plane.position.x, false );

   this.InfoDialog.SetSResource( "Lat", lat_string);
   this.InfoDialog.SetSResource( "Lon", lon_string );
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.SelectPlane = function( /* int */ plane_index )
{
   if( this.SelectedPlaneIndex == -1 )
     // No previously selected plane: activate an information popup dialog.
     this.InfoDialog.SetDResource( "Visibility", 1.0 );
   else 
   {      
      /* The dialog is already active, just unselect the previously selected 
         plane.
      */
      let curr_selected_plane = this.Planes[ this.SelectedPlaneIndex ];
      if( !curr_selected_plane.destroyed )
        curr_selected_plane.glg_object.SetDResource( "SelectedState", 0.0 );
   }
      
   this.SelectedPlaneIndex = plane_index;
   let selected_plane = this.Planes[ plane_index ];
   
   // Display selected plane or missile info stored as its name.
   let plane_name = selected_plane.glg_object.GetSResource( "Name" );
   this.InfoDialog.SetSResource( "PlaneName", plane_name );

   // Set the SelectedState resource which controls an icon's color. 
   selected_plane.glg_object.SetDResource( "SelectedState", 1.0 );
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.UnselectPlane = function()
{
   if( this.SelectedPlaneIndex == -1 )
     return;

   let selected_plane = this.Planes[ this.SelectedPlaneIndex ];   
   if( !selected_plane.destroyed )
     selected_plane.glg_object.SetDResource( "SelectedState", 0.0 );
   
   this.InfoDialog.SetDResource( "Visibility", 0.0 );
   this.SelectedPlaneIndex = -1;
}

//////////////////////////////////////////////////////////////////////////
// Processes buttons' and sliders' events.
//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.InputCallback =
  function( /* GlgObject */ vp, /* GlgObject */ message_obj )
{
   if( !this.Active )
     return;     
    
   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );

   if( format == "Slider" )
   {
      if( action != "ValueChanged" )
        return;
      
      if( origin == "UpdateSpeed" )
        // Query the new value of the UpdateSpeed Slider (relative value).
        this.UpdateSpeed = message_obj.GetDResource( "ValueX" ); 
   }
   else if( format == "Button" )
   {
      if( action != "Activate" )
        return;
      
      if( origin == "Restart" )
      {
         /* Delete all planes, they will be recreated on the next update. */ 
         for( let i=0; i<this.NumPlanes; ++i )
           if( !this.Planes[ i ].destroyed )
             this.DeletePlane( i, true );

         this.Drawing.Update();
      }
      else if( origin == "BigPlanes" )  // Increase plane size
      {
         this.PlaneScale *= 1.5;
         this.AdjustPlanesScale( 1.5 );
      }
      else if( origin == "SmallPlanes" )  // Decrease plane size
      {
         this.PlaneScale /= 1.5;
         this.AdjustPlanesScale( 1.0 / 1.5 );
      }
      else if( origin == "ClosePlaneInfo" )
      {
         // Unselect the plane and close its information popup dialog.
         this.UnselectPlane();
         this.Drawing.Update();
      }
   }
   /* Process mouse clicks on plane icons, implemented as an Action with
      the PlaneSelection label attached to an icon and activated on a 
      mouse click. 
   */
   else if( format == "CustomEvent" )
   {
      let event_label = message_obj.GetSResource( "EventLabel" );   /* String */
      
      /* Plane icon was selected */
      if( event_label == "PlaneSelection" )
      {
         // Get plane index (int).
         let plane_index =
           Math.trunc( message_obj.GetDResource( "Object/PlaneIndex" ) );
         this.SelectPlane( plane_index );
         this.Drawing.Update(); 
      }
   }
}

//////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.AdjustPlanesScale = function( /* double */ factor )
{
   const MAX_PLANE_SCALE = 3.375;
   let scale;   /* double */
   
   for( let i=0; i<this.NumPlanes; ++i )
   {
      let plane = this.Planes[ i ];

      if( !plane.destroyed )
      {
         scale = plane.glg_object.GetDResource( "Scale" );
         scale *= factor;        
         if( scale > MAX_PLANE_SCALE )
           scale = MAX_PLANE_SCALE;
         
         plane.glg_object.SetDResource( "Scale", scale );
      }
   }
   
   this.Drawing.Update();
}

//////////////////////////////////////////////////////////////////////////
// Generate a location info string by converting +- sign info into the
// N/S, E/W suffixes, and decimal fraction to deg, min, sec.
//////////////////////////////////////////////////////////////////////////
function CreateLocationString( /* double */ value,
                               /* boolean */ return_lat )   /* String */
{
   let deg, min, sec;    /* int */
   let suffix;           /* String */

   if( return_lat )
   {
      if( value < 0.0 )
      {
         value = -value;
         suffix = 'S';
      }
      else
        suffix = 'N';
      
      deg = Math.trunc( value );
      min = Math.trunc( ( ( value - deg ) * 60.0 ) );
      sec = Math.trunc( ( ( value - deg - min / 60.0 ) * 3600.0 ) );
      
      return ( "" + deg + "\u00B0" + padded( min ) + "'" + padded( sec ) +
               '"' + suffix );
   }
   else   // return lon
   {
      if( value < 0.0 )
      {
         value = -value;
         suffix = 'W';
      }
      else if( value >= 360.0 )
      {
         value -= 360.0;
         suffix = 'E';
      }
      else if( value >= 180.0 )
      {
         value = 180.0 - ( value - 180.0 );
         suffix = 'W';
      }
      else
        suffix = 'E';
      
      deg = Math.trunc( value );
      min = Math.trunc( ( ( value - deg ) * 60.0 ) );
      sec = Math.trunc( ( ( value - deg - min / 60.0 ) * 3600.0 ) );
      return ( "" + deg + "\u00B0" + padded( min ) + "'" + padded( sec ) +
               '"' + suffix );
   }
}

//////////////////////////////////////////////////////////////////////////
// Pads the value with 0 if needed to have a constant field width. 
//////////////////////////////////////////////////////////////////////////
function padded( /* int */ value )   /* String */
{
   if( value < 10 )
     return "0" + value;
   else
     return "" + value;
}

//////////////////////////////////////////////////////////////////////////
function IS_MISSILE( /* int */ plane )    /* boolean */
{
   /* Missiles use odd indices, planes use even ones. */
   return ( plane % 2 ) != 0;
}

//////////////////////////////////////////////////////////////////////////
function IS_PLANE( /* int */ plane )      /* boolean */
{
   return !IS_MISSILE( plane );
}

//////////////////////////////////////////////////////////////////////////
// Converts degrees to radians.
//////////////////////////////////////////////////////////////////////////
function RAD( /* double */ angle )   /* double */
{
   return angle / 180.0 * Math.PI;
}

//////////////////////////////////////////////////////////////////////////
// Converts radians to degrees.
//////////////////////////////////////////////////////////////////////////
function DEG( /* double */ angle )   /* double */
{
   return angle / Math.PI * 180.0;
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 800 / 700;
   
   const MIN_WIDTH = 600;
   const MAX_WIDTH = 1000;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgAirCombat.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      TextScale = 1.7;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.6;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}

//////////////////////////////////////////////////////////////////////////
// Support class, keeps data for one airplane or missile.
//////////////////////////////////////////////////////////////////////////
function Plane()
{
   this.glg_object = null;        /* GlgObject */
   this.speed = 0;                /* double */
   this.change_state = 0;         /* int */
   this.correcting_distance = 0;  /* int */

   /* True initially: no graphics created for the plane yet. */
   this.destroyed = true;         /* boolean */

   /* Prevents from changing for some time after a turn to let the plane 
      correct the distance.
   */
   this.missile_delay = 0;        /* int */

   this.position     = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.direction    = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.angle        = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.angle_change = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
}

///////////////////////////////////////////////////////////////////////
// A partial copy.
///////////////////////////////////////////////////////////////////////
Plane.prototype.CopyPlaneInfoFrom = function( /* Plane */ from )
{
   this.position.x = from.position.x;
   this.position.y = from.position.y;
   this.position.z = from.position.z;
   
   this.angle.x = from.angle.x;
   this.angle.y = from.angle.y;
   this.angle.z = from.angle.z;
   
   this.angle_change.x = from.angle_change.x;
   this.angle_change.y = from.angle_change.y;
   this.angle_change.z = from.angle_change.z;
   
   this.change_state = from.change_state;
   this.correcting_distance = from.correcting_distance;
}

/////////////////////////////////////////////////////////////////////////
// Support class, keeps data for one blast icon.
//////////////////////////////////////////////////////////////////////////
function Blast()
{
   this.glg_object = null;        /* GlgObject */
   this.age = BLAST_DURATION;
}
